// ============================================================================
// Regex-based Python Code Parser Utilities
// ============================================================================

import type { ConditionBlockBoundary, ExtractedCondition } from '../types';

/**
 * Removes comments from Python code while preserving the structure
 * @param {string} code - The Python code to remove comments from
 * @return {string} Code with comments removed
 */
// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: existing
function removeComments(code: string): string {
  const lines = code.split('\n');
  const cleanedLines: string[] = [];

  for (const line of lines) {
    const trimmedLine = line.trim();
    if (trimmedLine.startsWith('#')) {
      continue; // Skip fully commented lines
    }

    // Handle inline comments - find # that's not inside a string
    let inString = false;
    let stringChar = '';
    let result = '';

    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      const prevChar = i > 0 ? line[i - 1] : '';

      if (!inString && (char === '"' || char === "'")) {
        if (
          i + 2 < line.length &&
          line[i + 1] === char &&
          line[i + 2] === char
        ) {
          inString = true;
          stringChar = char.repeat(3);
          result += char + char + char;
          i += 2;
          continue;
        }
        inString = true;
        stringChar = char;
        result += char;
      } else if (inString) {
        result += char;
        // Check for triple-quote string closure
        if (
          stringChar.length === 3 &&
          char === stringChar[0] &&
          prevChar !== '\\'
        ) {
          if (
            i + 2 < line.length &&
            line[i + 1] === char &&
            line[i + 2] === char
          ) {
            result += char + char;
            i += 2;
            inString = false;
            stringChar = '';
          }
        } else if (
          stringChar.length === 1 &&
          char === stringChar &&
          prevChar !== '\\'
        ) {
          inString = false;
          stringChar = '';
        }
      } else if (char === '#') {
        break;
      } else {
        result += char;
      }
    }

    cleanedLines.push(result);
  }

  return cleanedLines.join('\n');
}

/**
 * Checks if a line is commented (exported for use in other modules)
 * @param {string} line - The line to check
 * @return {boolean} True if the line is commented, false otherwise
 */
export function isCommentedLine(line: string): boolean {
  const trimmed = line.trim();
  return (
    trimmed.startsWith('#') ||
    trimmed.startsWith('"""') ||
    trimmed.startsWith("'''")
  );
}

/**
 * Uncomments a line (exported for use in other modules)
 * @param {string} line - The line to uncomment
 * @return {string} Uncommented line
 */
export function uncommentLine(line: string): string {
  const trimmed = line.trim();

  if (trimmed.startsWith('"""')) {
    let result = line.replace(/^(\s*)"""/, '$1');
    result = result.replace(/"""\s*$/, '');
    return result;
  }

  if (trimmed.startsWith("'''")) {
    let result = line.replace(/^(\s*)'''/, '$1');
    result = result.replace(/'''\s*$/, '');
    return result;
  }

  if (trimmed.startsWith('#')) {
    const originalIndent = line.match(/^(\s*)/)?.[1] || '';
    const match = line.match(/^(\s*)#+\s(.*)$/);
    if (match) {
      const [, indentBeforeHash, contentAfterHash] = match;
      return indentBeforeHash + contentAfterHash;
    }
    const afterComment = trimmed.replace(/^#+\s/, '');
    if (afterComment) {
      return originalIndent + afterComment;
    }
    return '';
  }

  return line;
}

/**
 * Converts a condition label to a Python variable name (mirrors toPythonicIdentifier).
 * Used for bare goto_step blocks where no explicit variable assignment exists.
 */
function labelToVariableName(label: string): string {
  return label
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_]/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '');
}

/**
 * Extracts string literal value from a string
 * @param {string} _text - The text to extract string literal from
 * @return {string | null} Extracted string literal or null if not found
 */
function _extractStringLiteral(_text: string): string | null {
  const match = _text.match(/^["']{1,3}([\s\S]*)["']{1,3}$/);
  return match ? match[1] : null;
}

/**
 * Comments out a line
 * @param {string} line - The line to comment out
 * @return {string} Commented line
 */
export function commentLine(line: string): string {
  const trimmed = line.trim();
  if (trimmed.length === 0) {
    return line;
  }
  if (trimmed.startsWith('#')) {
    return line; // Already commented
  }
  const indent = line.match(/^(\s*)/)?.[1] || '';
  return `${indent}# ${trimmed}`;
}

/**
 * Regex-based Python parser for condition blocks
 */
export class RegexParser {
  /**
   * Checks if code is valid (always returns true for regex parser)
   * @param {string} _code - The code to validate (unused, always returns true)
   * @return {boolean} Always returns true since regex parser doesn't validate syntax
   */
  isValid(_code: string): boolean {
    return true; // Regex parser doesn't validate syntax
  }

  /**
   * Extracts conditions from code using regex.
   * Scans every non-commented line for flow.goto_step(...) calls regardless of
   * surrounding code structure, and deduplicates by (stepName, label).
   * @param {string} code - The Python code to extract conditions from
   * @return {ExtractedCondition[]} Array of extracted conditions
   */
  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: existing
  extractConditions(code: string): ExtractedCondition[] {
    if (!code || code.trim().length === 0) {
      return [];
    }

    // Remove comments first (same as original parser.ts)
    const cleanedCode = removeComments(code);
    const lines = cleanedCode.split('\n');
    const conditions: ExtractedCondition[] = [];
    const seen = new Set<string>();

    // Flat scan for goto_step calls
    for (const line of lines) {
      const result = this.matchGotoStepCall(line);
      if (result) {
        const key = `${result.stepName}\0${result.label}`;
        if (!seen.has(key)) {
          seen.add(key);
          conditions.push({
            label: result.label,
            stepName: result.stepName,
          });
        }
      }
    }

    // Structural scan for exit flow: var = True|False, if var:, conv.exit_flow()
    const exitFlowSeen = new Set<string>();
    for (let i = 0; i < lines.length; i++) {
      const trimmed = lines[i].trim();

      // Look for variable assignment: variable_name = False (or True)
      const varMatch = trimmed.match(/^(\w+)\s*=\s*(?:True|False)\s*$/);
      if (!varMatch) {
        continue;
      }

      const variableName = varMatch[1];

      // Look for if statement on next lines
      const ifPattern = new RegExp(
        `^if\\s+${variableName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*:\\s*$`,
      );

      let ifLineIndex = -1;
      for (let j = i + 1; j < Math.min(i + 5, lines.length); j++) {
        if (ifPattern.test(lines[j].trim())) {
          ifLineIndex = j;
          break;
        }
      }

      if (ifLineIndex === -1) {
        continue;
      }

      // Check for conv.exit_flow() inside the if block
      const ifIndent = lines[ifLineIndex].match(/^(\s*)/)?.[1]?.length || 0;
      for (let j = ifLineIndex + 1; j < lines.length; j++) {
        const blockTrimmed = lines[j].trim();
        const blockIndent = lines[j].match(/^(\s*)/)?.[1]?.length || 0;

        if (blockIndent <= ifIndent && blockTrimmed.length > 0) {
          break;
        }

        if (/conv\.exit_flow\(\)/i.test(blockTrimmed)) {
          if (!exitFlowSeen.has(variableName)) {
            exitFlowSeen.add(variableName);
            conditions.push({
              label: variableName,
              isExitFlow: true,
            });
          }
          break;
        }
      }
    }

    return conditions;
  }

  /**
   * Finds all condition blocks in code (including commented ones)
   * @param {string} code - The Python code to find condition blocks in
   * @return {ConditionBlockBoundary[]} Array of condition block boundaries
   */
  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: existing
  findConditionBlocks(code: string): ConditionBlockBoundary[] {
    if (!code || code.trim().length === 0) {
      return [];
    }

    const lines = code.split('\n');
    const blocks: ConditionBlockBoundary[] = [];

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const trimmed = line.trim();
      const isCommented = isCommentedLine(trimmed);
      const uncommentedLine = isCommented
        ? uncommentLine(line).trim()
        : trimmed;

      // Look for variable assignment: variable_name = False (or True)
      const varMatch = uncommentedLine.match(/^(\w+)\s*=\s*(?:True|False)\s*$/);
      if (!varMatch) {
        continue;
      }

      const variableName = varMatch[1];

      // Look for if statement in next few lines
      let ifLineIndex = -1;
      const ifPattern = new RegExp(
        `^if\\s+${variableName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*:\\s*$`,
      );

      for (let j = i + 1; j < Math.min(i + 5, lines.length); j++) {
        const nextLine = lines[j];
        const nextTrimmed = nextLine.trim();
        const nextIsCommented = isCommentedLine(nextTrimmed);
        const nextUncommented = nextIsCommented
          ? uncommentLine(nextLine).trim()
          : nextTrimmed;

        if (ifPattern.test(nextUncommented)) {
          ifLineIndex = j;
          break;
        }
      }

      if (ifLineIndex === -1) {
        continue;
      }

      // Look for goto_step or exit_flow call
      // Get the indentation of the if line (use uncommented version if commented)
      const ifLine = lines[ifLineIndex];
      const ifLineTrimmed = ifLine.trim();
      const ifLineIsCommented = isCommentedLine(ifLineTrimmed);
      const ifLineUncommented = ifLineIsCommented
        ? uncommentLine(ifLine)
        : ifLine;
      const ifIndent = ifLineUncommented.match(/^(\s*)/)?.[1]?.length || 0;

      let gotoStepLineIndex = -1;
      let exitFlowLineIndex = -1;
      let stepName: string | undefined;
      let label: string | undefined;
      let isExitFlow = false;

      for (let j = ifLineIndex + 1; j < lines.length; j++) {
        const nextLine = lines[j];
        const nextTrimmed = nextLine.trim();
        const nextIsCommented = isCommentedLine(nextTrimmed);
        const nextUncommented = nextIsCommented
          ? uncommentLine(nextLine)
          : nextLine;

        // Use the uncommented line's indentation for comparison
        const nextIndent = nextUncommented.match(/^(\s*)/)?.[1]?.length || 0;

        // If we've gone back to same or less indentation, we're done
        if (nextIndent <= ifIndent && nextUncommented.trim().length > 0) {
          break;
        }

        // Check if this line contains the goto_step or exit_flow call (commented or not)
        // Remove leading/trailing whitespace for pattern matching
        const trimmedForPattern = nextUncommented.trim();

        // Check for exit_flow first
        if (/conv\.exit_flow\(\)/i.test(trimmedForPattern)) {
          exitFlowLineIndex = j;
          isExitFlow = true;
          label = variableName.replace(/_/g, ' ');
          break;
        }

        // Check for goto_step
        if (/flow\.goto_step\(/i.test(trimmedForPattern)) {
          gotoStepLineIndex = j;

          const gotoResult = this.matchGotoStepCall(trimmedForPattern);
          if (gotoResult) {
            stepName = gotoResult.stepName;
            label = gotoResult.label;
          } else {
            // If we found goto_step but couldn't extract, use variable name as label
            label = variableName.replace(/_/g, ' ');
          }
          break;
        }
      }

      const endLineIndex =
        gotoStepLineIndex !== -1
          ? gotoStepLineIndex
          : exitFlowLineIndex !== -1
            ? exitFlowLineIndex
            : ifLineIndex;

      if (endLineIndex !== -1 && (stepName || isExitFlow)) {
        // Determine if entire block is commented
        const varLineCommented = isCommented;
        const ifLineCommented = isCommentedLine(lines[ifLineIndex].trim());
        const endLineCommented = isCommentedLine(lines[endLineIndex].trim());
        const allCommented =
          varLineCommented && ifLineCommented && endLineCommented;

        blocks.push({
          variableName,
          stepName,
          label: label || variableName.replace(/_/g, ' '),
          startLine: i,
          endLine: endLineIndex,
          startIndex: 0, // Not used in regex parser
          endIndex: 0, // Not used in regex parser
          isCommented: allCommented,
          isExitFlow,
        });
      } else if (
        gotoStepLineIndex === -1 &&
        exitFlowLineIndex === -1 &&
        ifLineIndex !== -1
      ) {
        // Skeleton block: var = False + if var: but goto_step/exit_flow was
        // removed entirely (not just commented). Record it so the sync handler
        // can detect that the condition block exists but is non-functional.
        blocks.push({
          variableName,
          stepName: undefined,
          label: variableName.replace(/_/g, ' '),
          startLine: i,
          endLine: ifLineIndex,
          startIndex: 0,
          endIndex: 0,
          isCommented: false,
          isExitFlow: false,
        });
      }
    }

    // Second pass: detect bare goto_step calls not consumed by structural blocks.
    // Only detects calls at the function body level (not inside if/for/while/etc. blocks).
    const consumedLines = new Set<number>();
    for (const block of blocks) {
      for (let lineIdx = block.startLine; lineIdx <= block.endLine; lineIdx++) {
        consumedLines.add(lineIdx);
      }
    }

    for (let i = 0; i < lines.length; i++) {
      if (consumedLines.has(i)) {
        continue;
      }

      const line = lines[i];
      const trimmed = line.trim();
      const lineIsCommented = isCommentedLine(trimmed);
      const lineToCheck = lineIsCommented
        ? uncommentLine(line).trim()
        : trimmed;

      const result = this.matchGotoStepCall(lineToCheck);
      if (!result) {
        continue;
      }

      // Check if this line is inside a block (not at function body level).
      // Look backward for the nearest non-empty line at strictly lower indentation.
      // If it ends with ':' and is not a def/class, the goto_step is inside a block.
      const lineIndent = line.match(/^(\s*)/)?.[1]?.length || 0;
      let insideBlock = false;
      for (let j = i - 1; j >= 0; j--) {
        const prevTrimmed = lines[j].trim();
        if (prevTrimmed === '') {
          continue;
        }
        const prevIndent = lines[j].match(/^(\s*)/)?.[1]?.length || 0;
        if (prevIndent < lineIndent) {
          const prevUncommented = isCommentedLine(prevTrimmed)
            ? uncommentLine(lines[j]).trim()
            : prevTrimmed;
          if (
            prevUncommented.endsWith(':') &&
            !/^(?:def |class )/.test(prevUncommented)
          ) {
            insideBlock = true;
          }
          break;
        }
      }

      if (insideBlock) {
        continue;
      }

      blocks.push({
        variableName: labelToVariableName(result.label),
        stepName: result.stepName,
        label: result.label,
        startLine: i,
        endLine: i,
        startIndex: 0,
        endIndex: 0,
        isCommented: lineIsCommented,
        isExitFlow: false,
      });
    }

    // Sort blocks by startLine for consistent ordering
    blocks.sort((a, b) => a.startLine - b.startLine);

    return blocks;
  }

  // goto_step argument patterns: [regex, stepCaptureIdx, labelCaptureIdx, guard?]
  private static readonly GOTO_STEP_PATTERNS: Array<
    [RegExp, number, number, ((line: string) => boolean)?]
  > = [
    // flow.goto_step("step_name", label="condition_label")
    // biome-ignore format: more readable regex
    [/(?:return\s+)?flow\.goto_step\(["']([^"']+)["']\s*,\s*label\s*=\s*["']([^"']+)["']\)/i, 1, 2],

    // flow.goto_step("step_name", "label_name") - both positional
    // biome-ignore format: more readable regex
    [/(?:return\s+)?flow\.goto_step\(["']([^"']+)["']\s*,\s*["']([^"']+)["']\)/i, 1, 2, (l) => !l.includes('label=') && !l.includes('step=')],

    // flow.goto_step(label="condition_label", step="step_name")
    // biome-ignore format: more readable regex
    [/(?:return\s+)?flow\.goto_step\([^)]*label\s*=\s*["']([^"']+)["'][^)]*step\s*=\s*["']([^"']+)["']\)/i, 2, 1],

    // flow.goto_step(step="step_name", label="condition_label")
    // biome-ignore format: more readable regex
    [/(?:return\s+)?flow\.goto_step\([^)]*step\s*=\s*["']([^"']+)["'][^)]*label\s*=\s*["']([^"']+)["']\)/i, 1, 2],

    // flow.goto_step(label="condition_label", "step_name")
    // biome-ignore format: more readable regex
    [/(?:return\s+)?flow\.goto_step\([^)]*label\s*=\s*["']([^"']+)["'][^)]*,\s*["']([^"']+)["']\)/i, 2, 1],
  ];

  /**
   * Tries to match a goto_step call in a line, returning step name and label if found.
   * Handles all 5 argument patterns and optional `return` prefix.
   * @param {string} line - The line to check (should be trimmed)
   * @return {{ stepName: string; label: string } | null} Extracted step name and label, or null
   */
  private matchGotoStepCall(
    line: string,
  ): { stepName: string; label: string } | null {
    const trimmed = line.trim();

    for (const [
      regex,
      stepIdx,
      labelIdx,
      guard,
    ] of RegexParser.GOTO_STEP_PATTERNS) {
      if (guard && !guard(trimmed)) {
        continue;
      }
      const match = trimmed.match(regex);
      if (match) {
        return { stepName: match[stepIdx], label: match[labelIdx] };
      }
    }

    return null;
  }
}

// Export singleton instance
export const regexParser = new RegexParser();
